import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { Snackbar } from "@mui/material";

import {
  SUBMIT_VERIFICATION_INFO,
  SUBMITTED_IDENTITY_VERIFICATION,
} from "../graphql/mutations/verification/verification.mutations";
import { VERIFICATION_BY_ID } from "../graphql/queries/verification/verification.queries";
import VerificationInfo, {
  VerificationInfoFormData,
} from "../components/verification/verification-info";
import VerifyIdentityDocument from "../components/verification/verify-identity-document";
import Alert from "../components/alert/alert.component";
import type { TransactionPrice } from "@shared/types/transactionPrice";

import "../components/checkout/order-review.scss";

const POLL_INTERVAL_MS = 3000;

// Mirrors the route-state contract cart.tsx projects and cart-payment.tsx
// consumes: the selected transactions plus their summed total, carried
// through router state (window.history.state) rather than the URL.
interface CartTransaction {
  uuid: string;
  code: string;
  scenarioType: string;
  tierName: string;
  medallions: number;
  subtotal: number;
  transferAgentName: string | null;
  certificateSymbols: string[];
  price: TransactionPrice;
}

interface CartVerificationState {
  transactions: CartTransaction[];
  totalInCents: number;
}

interface EntrustConfiguration {
  verificationId: number;
  workflowRunId: string;
  sdkToken: string;
}

const CartVerificationPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as CartVerificationState | null;

  const [alertOpen, setAlertOpen] = useState(false);
  const [error, setError] = useState<string>("");
  const [entrustConfiguration, setEntrustConfiguration] =
    useState<EntrustConfiguration | null>(null);
  const [polling, setPolling] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<string | null>(null);
  // E.164 phone captured on the info form, forwarded to the phone-verification
  // step so it can show the masked number the OTP was sent to.
  const [phoneNumber, setPhoneNumber] = useState<string>("");

  // Same guard as cart-payment: if we landed here without a selection
  // (new tab, bookmarked URL, promo removed everything), send the
  // customer back to the cart rather than render an orphaned form.
  useEffect(() => {
    if (!state?.transactions?.length) {
      navigate("/cart", { replace: true });
    }
  }, []);

  // Mutation to submit verification info. On success, stash the Entrust
  // config (verificationId, workflowRunId, sdkToken) so we can hand off to
  // the document-capture step. Mirrors account-verification.tsx.
  const [submitVerificationInfo, { loading: submitLoading }] = useMutation(
    SUBMIT_VERIFICATION_INFO,
    {
      onCompleted: (data) => {
        // A verification that resolves immediately (e.g. APPROVED/SKIPPED)
        // comes back on the mutation itself; the redirect effect below picks
        // it up without waiting on the document-capture poll.
        setVerificationStatus(data.submitVerificationInfo.status ?? null);

        if (
          data.submitVerificationInfo.exists &&
          data.submitVerificationInfo.identityVerification
        ) {
          setEntrustConfiguration({
            verificationId: data.submitVerificationInfo.verificationId,
            workflowRunId:
              data.submitVerificationInfo.identityVerification.workflowRunId,
            sdkToken: data.submitVerificationInfo.identityVerification.sdkToken,
          });
        }
      },
      onError: (err) => {
        setError(err.message);
        setAlertOpen(true);
      },
    }
  );

  // Marks the identity verification as submitted once the document-capture
  // step finishes, mirroring transaction-verification.tsx.
  const [submitVerification] = useMutation(SUBMITTED_IDENTITY_VERIFICATION);

  // Poll for the verification result once the document step completes.
  // Skipped until `polling` flips true. Stop polling as soon as the
  // verification leaves IN_PROGRESS and record the resolved status; the
  // effect below decides where it leads.
  const { stopPolling } = useQuery(VERIFICATION_BY_ID, {
    variables: { verificationId: entrustConfiguration?.verificationId },
    skip: !polling || !entrustConfiguration,
    pollInterval: POLL_INTERVAL_MS,
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const status = data?.verificationById?.status;
      if (!status || status === "IN_PROGRESS") {
        return;
      }

      // Any resolved status stops the poll.
      stopPolling();
      setVerificationStatus(status);
    },
  });

  // Forward to phone verification as soon as the verification reaches a
  // payment-allowed status (mirrors the backend's
  // PAYMENT_ALLOWED_VERIFICATION_STATUSES), whether that status arrived from
  // the submit mutation or from the poll above. REJECTED/CANCELED fall
  // through to the error UI below.
  useEffect(() => {
    if (
      verificationStatus === "APPROVED" ||
      verificationStatus === "SKIPPED" ||
      verificationStatus === "NEEDS_REVIEW"
    ) {
      navigate("/cart/phone-verification", {
        state: {
          transactions: state?.transactions,
          totalInCents: state?.totalInCents,
          phoneNumber,
        },
      });
    }
  }, [verificationStatus]);

  if (!state?.transactions?.length) {
    return null;
  }

  const handleSubmit = async (formData: VerificationInfoFormData) => {
    // Convert phone to E.164 format
    const phoneDigits = formData.phoneNumber.replace(/\D/g, "");
    const e164Phone = `+1${phoneDigits}`;
    setPhoneNumber(e164Phone);

    try {
      await submitVerificationInfo({
        variables: {
          input: {
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            dateOfBirth: new Date(formData.dateOfBirth).toISOString(),
            phoneNumber: e164Phone,
            transactionUUIDs: state.transactions.map((t) => t.uuid),
          },
        },
      });
    } catch (err) {
      // Error handled by onError callback
    }
  };

  // Once the document-capture step finishes, mark identity verification as
  // submitted and start polling for the resolved status. Mirrors
  // transaction-verification.tsx's VerifyIdentityDocument onComplete.
  const handleDocumentComplete = () => {
    if (entrustConfiguration) {
      submitVerification({
        variables: {
          verificationId: entrustConfiguration.verificationId,
          transactionUUIDs: state?.transactions?.map((t) => t.uuid) ?? [],
        },
      });
    }
    setPolling(true);
  };

  // Restart verification from the info form, matching the "Try Again"
  // affordance transaction-verification.tsx offers on a canceled verification.
  const handleRetry = () => {
    setPolling(false);
    setEntrustConfiguration(null);
    setVerificationStatus(null);
  };

  const renderContent = () => {
    if (verificationStatus === "CANCELED") {
      return (
        <div className="order-review-error">
          <span className="fa fa-exclamation-triangle"></span>
          <p>Your identity verification was cancelled. Please try again.</p>
          <button className="btn-secondary" onClick={handleRetry}>
            Try Again
          </button>
        </div>
      );
    }

    if (verificationStatus === "REJECTED") {
      return (
        <div className="order-review-error">
          <span className="fa fa-exclamation-triangle"></span>
          <p>We were unable to verify your identity. Please contact support for assistance.</p>
        </div>
      );
    }

    if (entrustConfiguration) {
      return (
        <VerifyIdentityDocument
          inquiryId={entrustConfiguration.workflowRunId}
          sessionToken={entrustConfiguration.sdkToken}
          onComplete={handleDocumentComplete}
        />
      );
    }

    return <VerificationInfo onSubmit={handleSubmit} loading={submitLoading} />;
  };

  return (
    <>
      {renderContent()}

      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setAlertOpen(false)} severity="error" sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CartVerificationPage;
